import React, { useState, useEffect } from "react";

const CAGRCalculator = () => {
  const [initialValue, setInitialValue] = useState(10000);
  const [finalValue, setFinalValue] = useState(15000);
  const [years, setYears] = useState(5);
  const [months, setMonths] = useState(0);
  const [additionalInvestment, setAdditionalInvestment] = useState(0);
  const [investmentType, setInvestmentType] = useState("lumpsum");
  const [inflationRate, setInflationRate] = useState(3);
  const [currency, setCurrency] = useState("usd");
  const [result, setResult] = useState({
    cagr: "0",
    category: "",
    interpretation: "",
    advice: "",
    totalReturn: "",
    absoluteReturn: "",
  });

  // CAGR Calculation
  const calculateCAGR = () => {
    let initial = parseFloat(initialValue);
    let final = parseFloat(finalValue);
    let y = parseFloat(years);
    let m = parseFloat(months);
    let inflation = parseFloat(inflationRate);

    if (isNaN(initial) || initial <= 0 || isNaN(final) || final <= 0 || isNaN(y) || y < 0 || isNaN(m) || m < 0) return;
    if (y === 0 && m === 0) return;

    // Convert total time period to years
    let totalYears = y + (m / 12);

    // Calculate CAGR: ((Final Value / Initial Value)^(1/years)) - 1
    let cagrValue = (Math.pow(final / initial, 1 / totalYears) - 1) * 100;

    // Ensure reasonable range
    cagrValue = Math.max(-100, Math.min(1000, cagrValue));

    // Calculate total return percentage
    const totalReturnPercent = ((final - initial) / initial) * 100;

    // Calculate absolute return
    const absoluteReturn = final - initial;

    // Calculate real CAGR (adjusted for inflation)
    const realCAGR = ((1 + cagrValue / 100) / (1 + inflation / 100) - 1) * 100;

    // Determine category based on CAGR
    let category = "";
    let interpretation = "";
    
    if (cagrValue < 0) {
      category = "Negative Returns";
      interpretation = "Your investment has lost value over this period. This indicates capital erosion and suggests reviewing your investment strategy or asset allocation.";
    } else if (cagrValue < 5) {
      category = "Below Average";
      interpretation = "Your returns are below typical market averages. Consider diversifying your portfolio or exploring higher-growth investment opportunities while managing risk.";
    } else if (cagrValue < 10) {
      category = "Average Returns";
      interpretation = "Your investment is generating moderate returns, close to historical market averages. This is a solid foundation, with room for optimization through strategic rebalancing.";
    } else if (cagrValue < 15) {
      category = "Good Returns";
      interpretation = "You're achieving above-average returns! Your investment strategy is performing well. Continue monitoring and rebalancing to maintain this growth trajectory.";
    } else if (cagrValue < 25) {
      category = "Excellent Returns";
      interpretation = "Outstanding performance! Your investments are significantly outperforming market averages. Ensure proper diversification to protect these gains and manage risk.";
    } else {
      category = "Exceptional Returns";
      interpretation = "Extraordinary growth! These exceptional returns suggest either high-risk investments, excellent market timing, or concentrated positions. Consider risk management and profit-taking strategies.";
    }

    // Generate personalized advice
    let advice = "";
    if (cagrValue < 0) {
      advice = "Review your investment allocation, consider dollar-cost averaging, and consult with a financial advisor to develop a recovery strategy. Focus on diversification and risk assessment.";
    } else if (cagrValue < 8) {
      advice = "Consider increasing equity exposure if your risk tolerance allows, explore index funds for market-matching returns, and review expense ratios to minimize costs eating into returns.";
    } else if (cagrValue < 15) {
      advice = "Maintain your current strategy while periodically rebalancing your portfolio. Consider tax-loss harvesting opportunities and ensure your asset allocation aligns with your long-term goals.";
    } else {
      advice = "Excellent performance! Ensure adequate diversification to protect gains, consider taking some profits if positions are heavily concentrated, and maintain discipline during market volatility.";
    }

    const currencySymbol = currency === "usd" ? "$" : currency === "eur" ? "€" : currency === "gbp" ? "£" : "₹";
    
    const totalReturnDisplay = `${totalReturnPercent.toFixed(2)}%`;
    const absoluteReturnDisplay = `${currencySymbol}${absoluteReturn.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const realCAGRDisplay = `${realCAGR.toFixed(2)}%`;

    setResult({
      cagr: `${cagrValue.toFixed(2)}%`,
      category,
      interpretation,
      advice,
      totalReturn: totalReturnDisplay,
      absoluteReturn: absoluteReturnDisplay,
      realCAGR: realCAGRDisplay,
      inflationImpact: `${(cagrValue - realCAGR).toFixed(2)}%`,
    });
  };

  useEffect(() => {
    calculateCAGR();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "white" }}>
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
          <h1>CAGR Calculator</h1>
          <p>
            Calculate your Compound Annual Growth Rate (CAGR) - the rate of return that would be required for an investment to grow from its beginning balance to its ending balance, assuming profits were reinvested.
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Investment Details
              </h2>

              {/* Initial Value Input */}
              <div className="input-group">
                <label>Initial Investment</label>
                <div className="input-wrapper">
                  <span className="input-prefix">💰</span>
                  <input
                    type="number"
                    value={initialValue}
                    onChange={(e) => setInitialValue(e.target.value)}
                    className="calc-input"
                    placeholder="10000"
                    min="1"
                  />
                </div>
                <div className="quick-select-group">
                  {[5000, 10000, 25000, 50000, 100000].map((val) => (
                    <button
                      key={val}
                      onClick={() => setInitialValue(val)}
                      className="quick-select-btn primary"
                    >
                      {val.toLocaleString()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Final Value Input */}
              <div className="input-group">
                <label>Final Value</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📈</span>
                  <input
                    type="number"
                    value={finalValue}
                    onChange={(e) => setFinalValue(e.target.value)}
                    className="calc-input"
                    placeholder="15000"
                  />
                </div>
                <div className="quick-select-group">
                  {[15000, 20000, 30000, 50000, 150000].map((val) => (
                    <button
                      key={val}
                      onClick={() => setFinalValue(val)}
                      className="quick-select-btn primary"
                    >
                      {val.toLocaleString()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Years Input */}
              <div className="input-group">
                <label>Time Period (Years)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📅</span>
                  <input
                    type="number"
                    value={years}
                    onChange={(e) => setYears(e.target.value)}
                    className="calc-input"
                    placeholder="5"
                    min="0"
                  />
                </div>
                <div className="quick-select-group">
                  {[1, 3, 5, 10, 20].map((y) => (
                    <button
                      key={y}
                      onClick={() => setYears(y)}
                      className="quick-select-btn success"
                    >
                      {y}
                    </button>
                  ))}
                </div>
              </div>

              {/* Months Input */}
              <div className="input-group">
                <label>Additional Months</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📆</span>
                  <input
                    type="number"
                    value={months}
                    onChange={(e) => setMonths(e.target.value)}
                    className="calc-input"
                    placeholder="0"
                    min="0"
                    max="11"
                  />
                </div>
                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                  Add extra months to your investment period (0-11 months).
                </p>
              </div>

              {/* Inflation Rate */}
              <div className="input-group">
                <label>Annual Inflation Rate (%)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📊</span>
                  <input
                    type="number"
                    value={inflationRate}
                    onChange={(e) => setInflationRate(e.target.value)}
                    className="calc-input"
                    placeholder="3"
                    min="0"
                    step="0.1"
                  />
                </div>
                <div className="quick-select-group">
                  {[2, 3, 4, 5, 6].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setInflationRate(rate)}
                      className="quick-select-btn success"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                  Used to calculate real returns adjusted for purchasing power.
                </p>
              </div>

              {/* Investment Type */}
              <div className="input-group">
                <label>Investment Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "lumpsum", label: "Lump Sum" },
                    { value: "sip", label: "SIP" },
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setInvestmentType(type.value)}
                      className={`quick-select-btn ${investmentType === type.value ? "primary" : "secondary"}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Currency */}
              <div className="input-group">
                <label>Currency</label>
                <div className="quick-select-group">
                  {[
                    { value: "usd", label: "USD ($)" },
                    { value: "eur", label: "EUR (€)" },
                    { value: "gbp", label: "GBP (£)" },
                    { value: "inr", label: "INR (₹)" },
                  ].map((curr) => (
                    <button
                      key={curr.value}
                      onClick={() => setCurrency(curr.value)}
                      className={`quick-select-btn ${currency === curr.value ? "primary" : "secondary"}`}
                    >
                      {curr.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateCAGR} className="calculate-btn">
                Calculate CAGR
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 8v4l3 2"></path>
                </svg>
                Your Investment Performance
              </h2>

              <div className="result-card">
                {/* CAGR */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Compound Annual Growth Rate</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.cagr}</div>
                  <div className="result-subtext">{result.category}</div>
                </div>

                {/* Returns Breakdown */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginTop: "1rem" }}>
                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Total Return</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                          <polyline points="17 6 23 6 23 12"></polyline>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.5rem" }}>{result.totalReturn}</div>
                    <div className="result-subtext">percentage gain</div>
                  </div>

                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Absolute Gain</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="1" x2="12" y2="23"></line>
                          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.5rem" }}>{result.absoluteReturn}</div>
                    <div className="result-subtext">dollar amount</div>
                  </div>
                </div>

                {/* Inflation-Adjusted Returns */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginTop: "1rem" }}>
                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Real CAGR</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.5rem" }}>{result.realCAGR}</div>
                    <div className="result-subtext">after inflation</div>
                  </div>

                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Inflation Impact</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="17 11 12 6 7 11"></polyline>
                          <polyline points="17 18 12 13 7 18"></polyline>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.5rem" }}>{result.inflationImpact}</div>
                    <div className="result-subtext">erosion rate</div>
                  </div>
                </div>

                {/* Interpretation */}
                <div className="breakdown-section" style={{ marginTop: "1.5rem" }}>
                  <h3 className="breakdown-title">What This Means</h3>
                  <p style={{ fontSize: "0.9rem", color: "#065f46", fontWeight: "500", marginBottom: "0.75rem" }}>
                    {result.interpretation}
                  </p>
                  <p style={{ fontSize: "0.9rem", color: "#065f46" }}>
                    {result.advice}
                  </p>
                </div>

                {/* CAGR Categories Chart */}
                <div className="breakdown-section" style={{ marginTop: "1.5rem" }}>
                  <h3 className="breakdown-title">CAGR Performance Ranges</h3>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#fee2e2", borderRadius: "0.375rem", borderLeft: "4px solid #ef4444" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Negative Returns: Below 0%</div>
                    <div style={{ fontSize: "0.75rem", color: "#991b1b" }}>Capital loss - needs strategy review</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#fef3c7", borderRadius: "0.375rem", borderLeft: "4px solid #f59e0b" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Below Average: 0-5%</div>
                    <div style={{ fontSize: "0.75rem", color: "#92400e" }}>Below typical market performance</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#dbeafe", borderRadius: "0.375rem", borderLeft: "4px solid #3b82f6" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Average: 5-10%</div>
                    <div style={{ fontSize: "0.75rem", color: "#1e40af" }}>Matches historical market averages</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#d1fae5", borderRadius: "0.375rem", borderLeft: "4px solid #34d399" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Good: 10-15%</div>
                    <div style={{ fontSize: "0.75rem", color: "#065f46" }}>Above-average performance</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#dcfce7", borderRadius: "0.375rem", borderLeft: "4px solid #10b981" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Excellent: 15-25%</div>
                    <div style={{ fontSize: "0.75rem", color: "#065f46" }}>Strong outperformance</div>
                  </div>
                  <div style={{ padding: "0.75rem", backgroundColor: "#fae8ff", borderRadius: "0.375rem", borderLeft: "4px solid #a855f7" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Exceptional: 25%+</div>
                    <div style={{ fontSize: "0.75rem", color: "#581c87" }}>Extraordinary growth - monitor risk</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding CAGR</h3>
              <p>
                CAGR (Compound Annual Growth Rate) represents the smoothed annual rate at which an investment would have grown if it had grown at a steady rate over time. Unlike simple returns, CAGR accounts for the compounding effect and provides a more accurate picture of investment performance.
                It's especially useful for comparing different investments or tracking portfolio performance across varying time periods. CAGR eliminates the volatility of year-to-year returns and shows what your consistent annual return would need to be.
                This metric is widely used by investors, financial analysts, and wealth managers to evaluate long-term investment performance and make informed decisions about asset allocation and portfolio rebalancing.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>CAGR Formula:</h4>
            <div className="formula-display">
              CAGR = [(Final Value / Initial Value)^(1 / Number of Years)] - 1
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Real CAGR = [(1 + CAGR) / (1 + Inflation Rate)] - 1
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Expressed as a percentage to show annualized growth rate
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Initial Value</div>
                <div className="formula-param-value">Starting investment</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Final Value</div>
                <div className="formula-param-value">Ending balance</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Time Period</div>
                <div className="formula-param-value">Years invested</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Compounding</div>
                <div className="formula-param-value">Annual basis</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is a good CAGR for investments?</h5>
              <p>
                Historically, the S&P 500 has returned about 10% annually. A CAGR of 8-12% is generally considered good for long-term stock investments. However, "good" depends on asset class, risk level, and market conditions. Real estate might average 8-10%, while safer bonds typically return 3-5%.
              </p>
            </div>

            <div className="faq-item">
              <h5>How is CAGR different from average return?</h5>
              <p>
                Average return simply adds up annual returns and divides by years, which can be misleading with volatility. CAGR shows the smoothed rate you'd need each year to reach your final value, accounting for compounding. It's more accurate for measuring actual growth over time.
              </p>
            </div>

            <div className="faq-item">
              <h5>Why should I consider inflation-adjusted CAGR?</h5>
              <p>
                Real CAGR (adjusted for inflation) shows your actual purchasing power growth. A 10% CAGR with 3% inflation means your real return is about 6.8%. This matters for long-term planning - you need returns that outpace inflation to truly build wealth.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can CAGR be used for investments with contributions?</h5>
              <p>
                CAGR works best for lump-sum investments. For regular contributions (like SIPs or 401k), use XIRR or IRR instead. CAGR assumes a single initial investment and doesn't account for cash flows during the period.
              </p>
            </div>

            <div className="faq-item">
              <h5>What CAGR should I target for retirement planning?</h5>
              <p>
                For retirement planning spanning 20-30 years, target 7-10% CAGR with a diversified portfolio. Conservative portfolios (more bonds) might aim for 5-7%, while aggressive (mostly stocks) could target 10-12%. Always factor in inflation and your risk tolerance.
              </p>
            </div>

            <div className="faq-item">
              <h5>Does higher CAGR always mean better investment?</h5>
              <p>
                Not necessarily. Higher CAGR often comes with higher risk and volatility. A 20% CAGR might involve significant ups and downs, while 8% might be more stable. Consider risk-adjusted returns, volatility, and whether you can handle the emotional stress of market swings.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CAGRCalculator;